/**
 * "Add a component" as a searchable popup. Shared by the unit page and the library page.
 *
 * It used to be an inline card listing every family and its templates, which on this install
 * is ten groups and about thirty templates — a wall of options below the unit's actual
 * content, pushing the thing the author came to look at off the screen. Adding a component is
 * a decision, not a permanent fixture of the page, so it belongs in a surface that opens and
 * closes. A library got a plain `<select>` instead, which is the same decision offered as
 * furniture.
 *
 * Search is the important part. A palette of thirty entries is faster to type into than to
 * scan, and it also solves the discoverability problem the old grouping had: "drag and drop"
 * lives under an Advanced disclosure that nobody opens, but it is one keystroke away from
 * anyone who knows the words.
 *
 * EVERY TEMPLATE IS OFFERED, and that is the fix for a measured defect rather than a
 * preference. This used to render only each family's FIRST template (`primary`, index 0 in
 * its group), which is harmless while every family has one entry and destructive as soon as
 * one has eight. Measured on `SUST102`: the platform returned 10 groups and 28 templates,
 * and the palette drew 9 tiles plus "Show 1 advanced type" — 10 of 28. The Advanced family
 * (8 templates) contributed ONE tile, Text (6) one, Open Response (5) one, Problem (3) one.
 * Poll, Survey, Word cloud, LTI and PDF are enabled on every course on this deployment with
 * no configuration at all (`DEFAULT_ADVANCED_MODULES`), so nothing was restricting them —
 * this component was hiding them, reachable only by typing a word you would have to already
 * know, because search returns before the filter.
 *
 * So the disclosure now hides only what it says it hides (the Advanced family and
 * deprecated types), the count on it counts templates rather than families, and a family
 * with several starting points gets a heading and its own tiles. The docstring's earlier
 * lesson still holds — an INLINE wall of thirty options buried the unit's content — and the
 * answer to thirty options is a dialog that groups and scrolls, not a list that hides.
 *
 * THE COMPONENT IS SHARED, AND SO IS THE SHAPING. Nothing here knows the name of a single
 * block type, and nothing here decides what a tile list looks like either — flattening,
 * the capability filter, the disclosure, the search ranking and the grouping decision all
 * live in `shared/lib/palette.ts` and are used by both pages through one function. That is
 * deliberate and it is the fix for the underlying defect: this component and the library
 * page each used to shape their own list, which is exactly how one surface came to show ten
 * tiles and the other twenty-eight from lists of the same size.
 *
 * SO ARE THE ICONS, and they were the last thing that was not. This component took an
 * `iconFor` prop; the library passed its own richer mapping and the unit page passed
 * nothing, so the same eight block types — Poll, Survey, Word cloud, LTI, PDF, Google
 * Document, Google Calendar — drew seven distinct glyphs in a library and eight identical
 * `widgets` in a course, from category strings that are byte-identical on both endpoints.
 * A prop a caller can forget is a difference waiting to happen, so there is no prop: the
 * mapping is `blockIcon` in `shared/lib/unit.ts`, one function, asked directly. An icon is
 * not a data source, and the data source is the only thing that is legitimately per-surface.
 *
 * What stays per-surface is only the DATA SOURCE — `container_handler`'s
 * `component_templates` for a unit, `block_types/` for a library — because those are two
 * endpoints with two payload shapes. The adapters for both are in that same file.
 */

import { useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Badge, Dialog, Icon, SearchField } from '../../shared/ui';
import {
  blockIcon,
  DEPRECATED_CATEGORIES,
  PICKER_CATEGORY,
  type ComponentTemplate,
  type ComponentTemplateGroup,
} from '../../shared/lib/unit';
import { disclosureCount, paletteSections } from '../../shared/lib/palette';
import './palette-picker.css';

export function PalettePicker({
  groups,
  pending,
  error,
  onPick,
  onClose,
  onOpenLibrary,
  title = 'Add a component',
  advancedNote = 'Advanced types come from module types an operator enabled on this platform. Support varies, and some cannot be changed once a learner has answered them.',
}: {
  groups: ComponentTemplateGroup[];
  /** Busy token of the pick in flight, so only that tile shows as working. */
  pending: string | null;
  /**
   * A failed pick, rendered INSIDE the dialog.
   *
   * Not a nicety. This is a native `<dialog>`, so it renders in the top layer and
   * everything behind it is covered and inert — a page-level error banner under an open
   * palette is a message the author cannot see. Both callers keep the palette open across
   * a failure (that is the point of it), so the explanation has to come in here with it.
   */
  error?: ReactNode;
  onPick: (template: ComponentTemplate) => void;
  onClose: () => void;
  /** Offered alongside the types: reuse something already written, rather than start blank. */
  onOpenLibrary?: () => void;
  title?: string;
  /** What "advanced" means here, which differs by surface — a course restricts its own list. */
  advancedNote?: string;
}) {
  const [query, setQuery] = useState('');
  const [advanced, setAdvanced] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  const needle = query.trim().toLowerCase();

  // Every decision about WHICH tiles and in what order is in shared/lib/palette.ts, so the
  // unit page and the library page cannot answer it differently. All this component decides
  // is what a tile looks like.
  const sections = useMemo(
    () => paletteSections(groups, { query, advanced }),
    [groups, query, advanced],
  );

  const shownCount = sections.reduce((total, section) => total + section.entries.length, 0);

  // TEMPLATES, not families. This used to count `primary` entries, so it always read
  // "Show 1 advanced type" while eight were hidden — the label understated the hidden set
  // eightfold, which is worse than no label.
  const advancedCount = disclosureCount(groups);

  return (
    <Dialog title={title} wide onClose={onClose}>
      <div className="palette">
        {error}

        <div className="palette__search">
          <SearchField
            value={query}
            onChange={setQuery}
            placeholder="Search component types — text, video, problem…"
          />
        </div>

        {onOpenLibrary && !needle && (
          <button type="button" className="palette__library" onClick={onOpenLibrary}>
            <span className="palette__library-icon">
              <Icon name="library_books" />
            </span>
            <span>
              <strong>Use content from a library</strong>
              <small>
                Insert something already written and keep it linked, so updates flow through.
              </small>
            </span>
            <Icon name="chevron_right" />
          </button>
        )}

        <div className="palette__list" ref={listRef}>
          {sections.map((section) => (
            <section className="palette__group" key={section.key}>
              {section.title && <h3 className="palette__group-title">{section.title}</h3>}
              <div className="palette__grid">
                {section.entries.map(({ group, template, primary }) => {
                  const token = `add:${template.category}:${template.boilerplate_name ?? ''}`;
                  const busy = pending === token;
                  return (
                    <button
                      type="button"
                      key={token + template.display_name}
                      className="palette__tile"
                      data-busy={busy || undefined}
                      aria-busy={busy || undefined}
                      data-deprecated={DEPRECATED_CATEGORIES.has(template.category) || undefined}
                      disabled={pending !== null}
                      // The library tile is not a block type — picking it opens the picker. Posting
                      // a create for it would make an uneditable HiddenBlock.
                      onClick={() =>
                        template.category === PICKER_CATEGORY && onOpenLibrary
                          ? onOpenLibrary()
                          : onPick(template)
                      }
                    >
                      <span className="palette__tile-icon" data-type={template.category}>
                        <Icon name={blockIcon(template.category)} />
                      </span>
                      <span className="palette__tile-body">
                        <strong>{template.display_name}</strong>
                        {/* Said in words, not only in the pulsing icon. A structural write to
                            this platform takes seconds (measured: 4.7 s for a component, 18.7 s
                            for a section), and an animation alone does not tell an author that
                            the thing they clicked is the thing being made. */}
                        {busy && <small className="palette__busy">Adding…</small>}
                        {DEPRECATED_CATEGORIES.has(template.category) && (
                          <small className="palette__deprecated">
                            Deprecated — use Problem Bank instead
                          </small>
                        )}
                        {/* Only where no heading already says it: worth saying in the flat
                            search results, redundant under a family's own heading. And never when
                            it is the template's own name twice. */}
                        {section.showFamily &&
                          !primary &&
                          group.display_name !== template.display_name && (
                            <small>{group.display_name}</small>
                          )}
                      </span>
                      {group.beta && <Badge tone="info">Beta</Badge>}
                    </button>
                  );
                })}
              </div>
            </section>
          ))}

          {shownCount === 0 && (
            <p className="palette__empty">
              Nothing matches &ldquo;{query}&rdquo;. Try a shorter word, or clear the search to
              browse every type.
            </p>
          )}
        </div>

        {!needle && advancedCount > 0 && (
          <button
            type="button"
            className="palette__toggle"
            onClick={() => setAdvanced(!advanced)}
            aria-expanded={advanced}
          >
            <Icon name={advanced ? 'expand_less' : 'expand_more'} />
            {/* "more types" rather than "advanced types": the disclosure also holds
                deprecated ones, and a label that names only half of what it hides is how
                "Show 1 advanced type" came to stand in front of eight. */}
            {advanced
              ? 'Show fewer types'
              : `Show ${advancedCount} more ${advancedCount === 1 ? 'type' : 'types'}`}
          </button>
        )}

        {advanced && !needle && (
          <p className="palette__note">{advancedNote}</p>
        )}
      </div>
    </Dialog>
  );
}
